import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { getCurrentUser } from "@/lib/auth";
import type { User } from "@/lib/auth";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedUserTypes?: ("investor" | "client")[];
}

export default function ProtectedRoute({
  children,
  allowedUserTypes,
}: ProtectedRouteProps) {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error("Auth check failed:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  // Still loading
  if (loading) {
    return (
      <div className="h-screen w-full bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin" />
          <p className="text-white font-mono text-sm">
            Checking authentication...
          </p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Check user type restriction
  if (allowedUserTypes && !allowedUserTypes.includes(user.user_type)) {
    // Redirect to correct dashboard based on user type
    const redirectPath =
      user.user_type === "investor"
        ? "/investor/dashboard"
        : "/client/dashboard";
    return <Navigate to={redirectPath} replace />;
  }

  // Authenticated and authorized
  return <>{children}</>;
}
